import { useCallback, useEffect, useState } from 'react';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { SETTINGS_PAGE } from './settings-shared';

/**
 * The Push notifications setting (Figma 11356-180894, "Access & sharing").
 *
 * The contract is the native apps' one, and the important half of it is what the toggle does NOT do:
 * **it never flips delivery from inside the app.** On iOS/Android the Settings switch reflects the
 * real effective permission and both directions hand off to the OS settings screen
 * (`SymphonyPush.openSystemSettings`, see `SettingsScreen.notificationsCard` on both platforms) —
 * a switch that pretended to own the permission would disagree with the OS the moment the user
 * changed it there.
 *
 * The browser's equivalent of "the OS owns this" is the Notification permission, so:
 *
 * | permission | turning ON                                   | turning OFF        |
 * |------------|----------------------------------------------|--------------------|
 * | `default`  | `requestPermission()` — the browser's prompt  | n/a (already off)  |
 * | `denied`   | cannot re-prompt → `blocked`                  | n/a (already off)  |
 * | `granted`  | already on                                    | → `browser-managed`|
 *
 * `browser-managed` and `blocked` are the two cases the page can only *explain*: no web API revokes
 * a granted permission, and a denied one cannot be re-prompted. That mirrors native, where the
 * switch also cannot change the permission itself — it just has an OS screen to send you to and we
 * do not.
 *
 * The permission is re-read whenever the tab comes back, because it is routinely changed in browser
 * UI while this page sits open — native re-reads on return for exactly the same reason.
 *
 * ⚠️ This governs the PERMISSION only. Web has no push transport yet (no service worker, no Ping
 * registration — see `SymphonyPush.registerWithPing` for what native does with its device token), so
 * a granted permission does not yet mean a delivered notification. Tracked in orion-web/CLAUDE.md.
 */
export type PushToggleOutcome = 'enabled' | 'blocked' | 'browser-managed' | 'unsupported';

function permissionOf(): NotificationPermission | null {
  if (typeof window === 'undefined' || !('Notification' in window)) return null;
  return Notification.permission;
}

export interface PushNotificationsState {
  /** False where the browser has no Notification API at all — the row is not rendered then. */
  supported: boolean;
  enabled: boolean;
  toggle: (next: boolean) => Promise<PushToggleOutcome>;
}

export function usePushNotifications(): PushNotificationsState {
  const { logPageClickBi } = useGenericBi();
  // Resolved after mount: `Notification.permission` does not exist during SSR, and reading it in
  // the initial state would render a different first paint on the server than in the browser.
  const [permission, setPermission] = useState<NotificationPermission | null>(null);

  useEffect(() => {
    setPermission(permissionOf());
    const sync = () => setPermission(permissionOf());
    window.addEventListener('visibilitychange', sync);
    window.addEventListener('focus', sync);
    return () => {
      window.removeEventListener('visibilitychange', sync);
      window.removeEventListener('focus', sync);
    };
  }, []);

  const toggle = useCallback(
    async (next: boolean): Promise<PushToggleOutcome> => {
      logPageClickBi({ ...SETTINGS_PAGE, button_name: next ? 'enable_notifications' : 'disable_notifications' });
      const current = permissionOf();
      if (current === null) return 'unsupported';
      if (!next) return 'browser-managed';
      if (current === 'granted') {
        setPermission('granted');
        return 'enabled';
      }
      if (current === 'denied') return 'blocked';
      const granted = await Notification.requestPermission();
      setPermission(granted);
      return granted === 'granted' ? 'enabled' : 'blocked';
    },
    [logPageClickBi],
  );

  return { supported: permission !== null, enabled: permission === 'granted', toggle };
}
